<script>
import { computed, defineComponent } from 'vue';

export default defineComponent({
  name: 'FlowResultTable',
  props: {
    results: {
      type: Object,
      required: true,
    },
    title: {
      type: String,
      default: 'Flow Analysis',
    },
  },
  setup(props) {
    const displayData = computed(() => {
      const {
        TotalForwardVolume,
        TotalBackwardVolume,
        TotalVolume,
        RegurgitationFraction,
        VolPerMin,
        VolPerMinEffective,
        HeartRate,
        MaxPressureGradient,
        MeanPressureGradient,
        MaximumVelocity,
        MinimumVelocity,
        MaximumAcceleration,
        MinimumAcceleration,
        MaximumFlow,
        MinimumFlow,
      } = props.results;

      const data = {
        TotalForwardVolume,
        TotalBackwardVolume,
        TotalVolume,
        RegurgitationFraction,
        VolPerMin,
        VolPerMinEffective,
        HeartRate,
        MaxPressureGradient,
        MeanPressureGradient,
        MaximumVelocity,
        MinimumVelocity,
        MaximumAcceleration,
        MinimumAcceleration,
        MaximumFlow,
        MinimumFlow,
      };

      return data;
    });

    // Create a computed property for groupDividers that depends on isSax
    const groupDividers = computed(() => {
      // Dividers that are always present
      const dividers = [
        'RegurgitationFraction',
        'HeartRate',
        'MinimumVelocity',
      ];

      return dividers;
    });

    const formatLabel = (key) => {
      const labels = {
        TotalForwardVolume: 'Total Forward Volume',
        TotalBackwardVolume: 'Total Backward Volume',
        TotalVolume: 'Total Volume',
        RegurgitationFraction: 'Regurgitation Fraction',
        VolPerMin: 'Vol/min',
        VolPerMinEffective: 'Vol/min (effective)',
        HeartRate: 'Heart Rate',
        MaxPressureGradient: 'Max Pressure Gradient',
        MeanPressureGradient: 'Mean Pressure Gradient',
        MaximumVelocity: 'Maximum Velocity (1px)',
        MinimumVelocity: 'Minimum Velocity (1px)',
        MaximumAcceleration: 'Maximum Acceleration',
        MinimumAcceleration: 'Minimum Acceleration',
        MaximumFlow: 'Maximum Flow',
        MinimumFlow: 'Minimum Flow',
      };

      return labels[key] || key;
    };

    const getUnitForKey = (key) => {
      if (key.includes('RegurgitationFraction')) return '%';
      if (key.includes('Volume')) return 'ml';
      if (key.includes('Velocity')) return 'cm/s';
      if (key.includes('Acceleration')) return 'cm/s/s';
      if (key.includes('Flow')) return 'ml/s';
      if (key.includes('PressureGradient')) return 'mmHg';
      if (key.includes('HeartRate')) return '/min';
      if (key.includes('VolPerMin')) return 'l/min';
      if (key.includes('VolPerMinEffective')) return 'l/min';
      return '';
    };

    const formatValue = (value, key) => {
      if (typeof value === 'number') {
        if (value === Infinity) return '0.0 ml'; // only esv

        // Check if this is a phase value - display as integer
        if (key.includes('Phase')) {
          // Special case: display dash for -1 phase values
          if (value === -1) return '-';
          return `${Math.round(value)}`;
        }

        // Other numeric values - display with decimal places
        return `${value.toFixed(1)} ${getUnitForKey(key)}`;
      }
      return String(value);
    };

    const isGroupDivider = (key) => {
      return groupDividers.value.includes(key);
    };

    return {
      displayData,
      formatLabel,
      formatValue,
      getUnitForKey,
      isGroupDivider,
      groupDividers,
    };
  },
});
</script>

<template>
  <div class="flow-result-table">
    <div class="header-row">
      <div class="header-cell">
        {{ title }}
      </div>
    </div>
    <div class="table-content">
      <table>
        <tbody>
          <tr
            v-for="(value, key) in displayData"
            :key="key"
            class="result-row"
            :class="{ 'group-divider': isGroupDivider(key) }"
          >
            <td class="result-label" :title="formatLabel(key)">
              {{ formatLabel(key) }}
            </td>
            <td class="result-value" :title="formatValue(value, key)">
              {{ formatValue(value, key) }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<style scoped>
.flow-result-table {
  position: relative;
  width: fit-content;
  min-width: 220px;
  border: 1px solid var(--md-surface-container-highest);
  border-radius: 8px;
  overflow: hidden;
  background-color: var(--md-surface-container);
  display: flex;
  flex-direction: column;
}

.header-row {
  display: flex;
  width: 100%;
  z-index: 2;
  background-color: var(--md-surface-container-high);
  border-bottom: 1px solid var(--md-surface-container-highest);
}

.header-cell {
  height: 28px;
  display: flex;
  align-items: center;
  padding: 0 8px;
  font-size: 0.75rem;
  font-weight: 600;
  width: 100%;
}

.table-content {
  width: 100%;
}

table {
  width: 100%;
  border-collapse: collapse;
}

.result-row {
  transition: background-color 0.15s ease;
}

.result-row.group-divider {
  border-bottom: 2px solid var(--md-surface-container-highest);
}

.result-row:last-child {
  border-bottom: none;
}

.result-row:hover {
  background-color: var(--md-surface-container-highest);
}

.result-label {
  padding: 6px 8px;
  font-size: 0.75rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-height: 28px;
  border-right: 1px solid var(--md-surface-container-highest);
  border-bottom: 1px solid var(--md-surface-container-highest);
  width: 80px;
}

.result-value {
  padding: 6px 12px 6px 8px;
  font-size: 0.75rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-height: 28px;
  width: 80px;
  font-family: monospace;
  text-align: right;
  border-bottom: 1px solid var(--md-surface-container-highest);
}

tr:last-child td {
  border-bottom: none;
}
</style>
